import React from 'react';
import PropTypes from 'prop-types';
import AvatarEditor from 'react-avatar-editor';
import {
  View,
  TouchableOpacity,
  Image,
} from 'react-native';
import { Box } from '@styleseat/ui';

import BodyText from '../../ui/BodyText';
import Slider from '../../ui/Slider';

import RotateIcon from '../../../../images/rotate.svg';

import './ImageEditor.module.scss';
import Feature from '../../Feature';

export const CROP_BORDERS = 40;

export const ImageEditorBase = ({
  image,
  zoom,
  rotate,
  position,
  cropViewport,
  onEditorMounted,
  onPositionChange,
  onImageLoaded,
  onZoomChanged,
  onRotateCW,
}) => (
  <View styleName="image-editor-container" testID="image-editor">
    {Boolean(cropViewport.height && cropViewport.width) && (
      <AvatarEditor
        styleName="image-editor"
        ref={onEditorMounted}
        image={image}
        height={cropViewport.height}
        width={cropViewport.width}
        scale={zoom}
        rotate={rotate}
        position={position}
        border={CROP_BORDERS}
        onPositionChange={onPositionChange}
        onLoadSuccess={onImageLoaded}
        crossOrigin="anonymous"
      />
    )}
    <View styleName="slider-container">
      <Slider
        value={zoom}
        minimumValue={1}
        maximumValue={2}
        step={0.01}
        title="Image zoom"
        onValueChange={onZoomChanged}
      />
    </View>
    <TouchableOpacity
      styleName="rotate"
      onPress={onRotateCW}
      testID="image-editor-rotate"
    >
      <Image source={RotateIcon} styleName="icon" />
      <BodyText emphasize styleName="rotate-text">
        Rotate
      </BodyText>
    </TouchableOpacity>
  </View>
);

/**
 * Renders a component which displays an editor supporting repositioning of an image within a crop
 * zone and provides zoom and rotation functionality.
 */
function ImageEditor(props) {
  const { image, allowRotate } = props;
  const imgUrl = typeof image === 'string'
    && `${image.replace(/base\.jpg$/, '')}200x200.jpg`;

  return (
    <Feature flag="pro-image-upload-edit-tools-disabled-20200806">
      <Feature.Disabled>
        <ImageEditorBase {...props} />
      </Feature.Disabled>
      <Feature.Enabled>
        {allowRotate && <ImageEditorBase {...props} />}
        {!allowRotate && (
          <Box alignItems="center" padding={8}>
            <Image source={imgUrl} style={{ width: 200, height: 200 }} />
          </Box>
        )}
      </Feature.Enabled>
    </Feature>
  );
}

const ImageEditorProps = {
  /** The image URL or file to edit. */
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(File),
  ]),
  /** Height and width of the viewport */
  cropViewport: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  /** Zoom factor on the image */
  zoom: PropTypes.number,
  /** Rotation in degrees */
  rotate: PropTypes.oneOf([
    0,
    90,
    180,
    270,
  ]),
  /** Position of center of viewport over image (each coordinate should be between 0 and 1
   * inclusive) */
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  /** callback when editor mounts  */
  onEditorMounted: PropTypes.func,
  /** callback on image  */
  onPositionChange: PropTypes.func.isRequired,
  /** callback on image load */
  onImageLoaded: PropTypes.func.isRequired,
  /** callback on new zoom level */
  onZoomChanged: PropTypes.func.isRequired,
  /** Rotates image clockwise */
  onRotateCW: PropTypes.func.isRequired,
};

const ImageEditorDefaultProps = {
  cropViewport: { height: 0, width: 0 },
  rotate: 0,
  zoom: 1,
  position: { x: 0.5, y: 0.5 },
  image: null,
  onEditorMounted: null,
};

ImageEditorBase.propTypes = ImageEditorProps;
ImageEditor.propTypes = { ...ImageEditorProps, allowRotate: PropTypes.bool };

ImageEditorBase.defaultProps = ImageEditorDefaultProps;
ImageEditor.defaultProps = { ...ImageEditorDefaultProps, allowRotate: false };

export default ImageEditor;
